import { useState, useCallback, useEffect } from 'react';
import type { ImpactResult } from './types';
import { fetchImpact, ApiError } from './api';
import { Results } from './components/Results';
import { API_BASE_URL } from './constants';
import { pushEvent, resultStatusFromError } from './analytics';
import type { MountOptions } from './mount';

type View = 'idle' | 'loading' | 'results' | 'error';

/** Loading copy, shown one stage at a time for STAGE_MS each. */
const LOADING_MESSAGES = ['Analysing your channel', 'Comparing to creators who use Lickd'];
const STAGE_MS = 2000;

export function App({ eventPrefix, signupRef }: MountOptions) {
  const [view, setView] = useState<View>('idle');
  const [loadingStage, setLoadingStage] = useState(0);
  const [channel, setChannel] = useState('');
  const [result, setResult] = useState<ImpactResult | null>(null);
  const [error, setError] = useState<{ title: string; msg: string }>({
    title: '',
    msg: '',
  });

  const handleSubmit = useCallback(
    async (e: React.SubmitEvent) => {
      const value = channel.trim();
      if (!value) return;

      e.preventDefault();
      // No channel value in the payload, so no personal data is sent.
      pushEvent(eventPrefix, 'channel_search');
      setView('loading');
      setLoadingStage(0);

      // Hold the loading state for the full staged sequence even when the API
      // answers instantly from cache, so the spinner doesn't flash past.
      const minDelay = new Promise((resolve) => setTimeout(resolve, STAGE_MS * LOADING_MESSAGES.length));

      try {
        const [res] = await Promise.all([fetchImpact(API_BASE_URL, value), minDelay]);
        setResult(res);
        setView('results');
        pushEvent(eventPrefix, 'results_shown', { result_status: 'success' });
      } catch (err) {
        const e = err as ApiError;
        pushEvent(eventPrefix, 'results_shown', { result_status: resultStatusFromError(e.status) });
        setError(
          e.status === 404
            ? {
                title: "Couldn't find that channel",
                msg: 'Try your @handle or full channel URL.',
              }
            : e.status === 422
              ? { title: 'Not enough data yet', msg: e.message }
              : e.status === 503
                ? {
                    title: 'Temporarily unavailable',
                    msg: 'Please try again in a moment.',
                  }
                : { title: 'Something went wrong', msg: e.message }
        );
        setView('error');
      }
    },
    [channel, eventPrefix]
  );

  // Step through the loading copy, parking on the last message if the API is
  // slower than the sequence.
  useEffect(() => {
    if (view !== 'loading' || loadingStage >= LOADING_MESSAGES.length - 1) return;
    const id = setTimeout(() => setLoadingStage((stage) => stage + 1), STAGE_MS);
    return () => clearTimeout(id);
  }, [view, loadingStage]);

  const reset = useCallback(() => {
    setView('idle');
    setResult(null);
  }, []);

  return (
    <div className="root">
      <div className="inner">
        {(view === 'idle' || view === 'loading') && (
          <div className="text-center">
            {view === 'idle' ? (
              <form className="search" onSubmit={handleSubmit}>
                <div className="search-inner">
                  <input
                    className="search-input"
                    id="youtube_channel"
                    name="youtube_channel"
                    type="text"
                    placeholder="Enter your YouTube channel or @handle"
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    aria-label="YouTube channel or handle"
                    aria-describedby="form_sub"
                  />
                  <button type="submit" className="btn btn-primary" disabled={!channel.trim()}>
                    Show my impact
                  </button>
                </div>
                <div className="form-sub" id="form_sub">
                  Takes 10 seconds. No sign-up needed.
                </div>
              </form>
            ) : (
              <div className="loading">
                <div className="spinner" />
                <div className="loading-txt" key={loadingStage}>
                  {LOADING_MESSAGES[loadingStage]}
                </div>
              </div>
            )}
          </div>
        )}

        {view === 'error' && (
          <div className="err">
            <div className="err-title">{error.title}</div>
            <div className="err-msg">{error.msg}</div>
            <button className="btn btn-secondary" onClick={reset}>
              Try again
            </button>
          </div>
        )}

        {view === 'results' && result && <Results result={result} signupRef={signupRef} onBack={reset} />}
      </div>
    </div>
  );
}
